import useSWR from "swr";
import { API_BASE_URL } from "@/lib/env";
import type { OneOffSession } from "@/api/client";

interface FetchError extends Error {
  status?: number;
}

export function useOneOffSession() {
  const { data, error, isLoading, mutate } = useSWR<OneOffSession>(
    `${API_BASE_URL}/me/one-off/sessions/current`,
    {
      // Poll every 3s when transcribing, otherwise no polling
      refreshInterval: (latestData) =>
        latestData?.status === "transcribing" ? 3000 : 0,
      // Don't retry 404s (no session is a valid state)
      onErrorRetry: (err: FetchError, _key, _config, revalidate, { retryCount }) => {
        if (err.status === 404) return;
        if (retryCount >= 2) return;
        setTimeout(() => revalidate({ retryCount }), 5000);
      },
    },
  );

  const fetchError = error as FetchError | undefined;
  const is404 = fetchError?.status === 404;

  return {
    session: is404 ? null : data ?? null,
    isLoading,
    isError: !!error && !is404,
    hasSession: !!data && !is404,
    refreshSession: mutate,
  };
}
